import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { AccessType, useWorkspaces } from 'zens-store'
import { Button, PopUp, Spacing } from 'zens-storybook'
import { Suspense, useState } from 'react'

import { usePermissions } from '@dms/hook'

import { WorkspaceFloorplanSelect } from '../workspace'
import { Panel } from '../layout'

import ZoneAddWorkspacesFloorplan, { ZoneWorkspace } from './addworkspacesfloorplan'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  popup: {
    padding: Spacing.M,
    minWidth: 900,
  },
})

interface Props extends ViewProps {
  companyId: number
  zoneId: number
  floorId?: number
  onChangeFloor?: (floorId: number) => void
  showAddWorkspaces?: boolean
  onSave?: (workspaces: ZoneWorkspace[]) => Promise<void>
}

const ZoneWorkspaceFloorplan = (props: Props) => {
  const { companyId, zoneId, floorId, onChangeFloor, showAddWorkspaces, onSave, style, ...otherProps } = props
  const [addWorkspacesVisible, setAddWorkspacesVisible] = useState(!!showAddWorkspaces)
  const { t } = useTranslation()
  const workspaces = useWorkspaces({
    companyId,
    filter: { zoneId: [zoneId] },
    pagination: { page: 0, size: 1000 },
  })
  const { hasPermission } = usePermissions(companyId)

  const saveWorkspaces = async (data: ZoneWorkspace[]) => {
    await onSave?.(data)
    workspaces.refetch()
    hideAddZones()
  }

  const showFloorplanSelect = () => {
    setAddWorkspacesVisible(true)
  }
  const hideAddZones = () => {
    setAddWorkspacesVisible(false)
  }
  return (
    <>
      <Panel
        style={[styles.container, style]}
        title={`${t('Workspaces')} (${workspaces.data?.meta?.total || 0})`}
        right={
          (hasPermission(AccessType.Update, 'workspace') && workspaces.data?.data?.length && (
            <Button
              title={t('Update workspaces')}
              left="pencil"
              onPress={showFloorplanSelect}
              testID="ZoneWorkspaceFloorplan.Edit"
            />
          )) ||
          undefined
        }
        {...otherProps}
      >
        <WorkspaceFloorplanSelect
          companyId={companyId}
          floorId={floorId}
          zoneId={zoneId}
          typeHref={(type) =>
            type === 'table'
              ? `/companies/${companyId}/workspaces/zones/${zoneId}`
              : `/companies/${companyId}/workspaces/zones/${zoneId}/floorplan`
          }
          onChangeFloor={onChangeFloor}
          onAdd={showFloorplanSelect}
        />
      </Panel>
      <PopUp visible={addWorkspacesVisible} onClose={hideAddZones} style={styles.popup} closeButton={false}>
        <Suspense fallback={<ZoneAddWorkspacesFloorplan.Skeleton />}>
          <ZoneAddWorkspacesFloorplan
            companyId={companyId}
            zoneId={zoneId}
            onSave={saveWorkspaces}
            onCancel={hideAddZones}
            floorId={floorId}
            onChangeFloor={onChangeFloor}
          />
        </Suspense>
      </PopUp>
    </>
  )
}

interface SkeletonProps extends ViewProps {
  companyId: number
}

const Skeleton = (props: SkeletonProps) => {
  const { companyId, style, ...otherProps } = props
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)
  return (
    <Panel.Skeleton
      style={[styles.container, style]}
      title={t('Workspaces')}
      right={hasPermission(AccessType.Update, 'workspace') && <Button.Skeleton />}
      {...otherProps}
    >
      <WorkspaceFloorplanSelect.Skeleton />
    </Panel.Skeleton>
  )
}
ZoneWorkspaceFloorplan.Skeleton = Skeleton

export default ZoneWorkspaceFloorplan
